import { inject, Injectable } from "@angular/core";
import { LocalStorageKeys, LocalStorageService, Unit } from "@optima-stride-ai/core";
import { DeliverableStateStoreService } from "../../deliverables/deliverable-state-store/deliverable-state-store.service";
import { KpiStateStoreService } from "../../kpi/KPI-state-stores/kpi-state-store.service";
import { IHomeQuickActions, IQuickActionEntity, IQuickActionMenuItems, ProgressStatusViewTypes, StatusIndicatorColor, ToastService } from "@optima-stride-ai/optima-components";
import { BehaviorSubject, Subscription } from "rxjs";
import { MeasureLogStateStoreService } from "../../transaction-log/measure-log-state-store/measure-log-state-store.service";
import { IDeliverable } from "../../deliverables/deliverable.model";
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework'; 
import { createMeasureLogJSON } from "../../kpi/auto-create-entites";
import { MeasuresStateStoreService } from "../../transaction(Measure)/measure-state-store/measures-state-store.service";
import { IMeasure, KPIAndMeasureCategories } from "../../transaction(Measure)/measure.model";
import { BenBizEntityHistoricalStatusStateStoreService } from "../../strategy/ben-biz-entity-historical-progress-status-store/ben-biz-entity-historical-status-state-store.service";
import { BenBizEntityProgressStatusStoreService } from "../../strategy/ben-biz-entity-progress-status-store/ben-biz-entity-progress-status-store.service";




@Injectable({
  providedIn: 'root'
})
export class QuickActionService {
  readonly storageService = inject(LocalStorageService);
  readonly deliverableStateStoreService = inject(DeliverableStateStoreService);
  readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  readonly kpiStateStoreService = inject(KpiStateStoreService);
  readonly measureStateStoreService = inject(MeasuresStateStoreService);
  readonly benBizEntityProgressStatusStoreService = inject(BenBizEntityProgressStatusStoreService);
  readonly benBizEntityHistoricalStatusStateStoreService = inject(BenBizEntityHistoricalStatusStateStoreService);
  readonly measureLogsStateStoreService = inject(MeasureLogStateStoreService);
  readonly toastService : ToastService = inject(ToastService);

  private homeQuickActionsSubject = new BehaviorSubject<IHomeQuickActions>({
    ProgressStatusViewTypes: ProgressStatusViewTypes.Detailed,
    isInCollection: false,
    status: [],
    checkIns: [],
    quickActions: []
  });
  homeQuickActions$ = this.homeQuickActionsSubject.asObservable();
    measureLogStoreSubscription: Subscription | undefined;
  ProgressStatusViewTypes = ProgressStatusViewTypes.Detailed;
  isInCollection = false;
  statusEntity: IQuickActionEntity[] = [];
  checkInsEntity: IQuickActionEntity[] = [];
  quickActionsEntity: IQuickActionMenuItems[] = [];
  measureData: IMeasure[] = [];
  constructor() {
    const storedData = this.storageService.read(LocalStorageKeys.QuickActions);
    if (storedData) {
      const parsed: IHomeQuickActions = JSON.parse(storedData);
      this.ProgressStatusViewTypes = parsed.ProgressStatusViewTypes;
      this.isInCollection = parsed.isInCollection;

      parsed.status.forEach((item) => {
        if (item.id ) {
          item.action = (event: any) => this.onProgressBarSaveClicked(event.key, event.id, event.value);
        }
      });

      this.statusEntity = parsed.status
      
      this.checkInsEntity = parsed.checkIns;
      this.quickActionsEntity = parsed.quickActions;

      
      this.homeQuickActionsSubject.next(parsed);
    } else {
      this.quickActionsEntity = [
        { icon: 'cilUserFollow', displayOrder: 1, title: "New Deliverable", callback: () => {} },
        { icon: 'cilSpeedometer', displayOrder: 2, title: "New Objective", callback: () => {}, disabled: true },
      ];
      this.updateHomeQuickActions();
    }

    if(this.measureLogsStateStoreService.changed)
    {
        this.measureLogStoreSubscription = this.measureLogsStateStoreService.changed.subscribe(() => {
          console.log("measure log changed - refreshing quick action status");
            this.fetchAndUpdateLatestStatus();
        });
    }
    this.fetchAndUpdateLatestStatus();
  }

  set status(value: IDeliverable[]) {
    this.statusEntity = value.map(transformedTovalue);
    this.updateHomeQuickActions();
  }
  get status(): IQuickActionEntity[] {
    return this.statusEntity;
  }

  set checkIns(value: IQuickActionEntity[]) {
    this.checkInsEntity = value;
    this.updateHomeQuickActions();
  }
  get checkIns(): IQuickActionEntity[] {
    return this.checkInsEntity;
  }

  set quickActions(value: IQuickActionMenuItems[]) {
    this.quickActionsEntity = value;
    this.updateHomeQuickActions();
  }
  get quickActions(): IQuickActionMenuItems[] {
    return this.quickActionsEntity;
  }

  set homeQuickActions(value: IHomeQuickActions) {
    this.ProgressStatusViewTypes = value.ProgressStatusViewTypes;
    this.isInCollection = value.isInCollection;
    this.statusEntity = value.status;
    this.checkInsEntity = value.checkIns;
    this.quickActionsEntity = value.quickActions;
    this.homeQuickActionsSubject.next(value);
    this.storageService.save(LocalStorageKeys.QuickActions, JSON.stringify(value));
  }
  get homeQuickActions(): IHomeQuickActions {
    this.updateHomeQuickActions();
    return this.homeQuickActionsSubject.getValue();
  }

  pushToStatus(value: IDeliverable, action?: (key: string, id: string, value: string) => void) {
    const statusValue = transformedTovalue(value);
    if (this.statusEntity.find(item => item.id === statusValue.id)){
      this.toastService.toast(`${statusValue.name} already exists in quick actions`);
      return;
    }
    if (action) {
      statusValue.action = (event: any) => action(event.key, event.id, event.value);
    }
    this.statusEntity.push(statusValue);
    this.updateHomeQuickActions();  
    this.toastService.toast(`${statusValue.name} added to quick actions`);
  }

  pushToCheckIns(value: IQuickActionEntity, action?: () => void) {
    this.checkInsEntity.push(value);
    this.updateHomeQuickActions();
    }

    pushToQuickActions(value: IQuickActionMenuItems) {
        this.quickActionsEntity.push(value);
        this.updateHomeQuickActions();
    }

    removeFromStatus(id: string) {
        const index = this.statusEntity.findIndex(item => item.id.toString() === id.toString());
        // console.log("removeFromStatus", id, index);
        if (index !== -1) {
            let entity = this.statusEntity[index];
            this.statusEntity.splice(index, 1);
            this.updateHomeQuickActions();
            this.toastService.toast(`${entity.name} removed from quick actions`);
        }
    }

    removeFromStatusByBizEntityId(id: string) {
        const index = this.statusEntity.findIndex(item => item.deliverableId?.toString() === id.toString());
        if (index !== -1) {
            this.statusEntity.splice(index, 1);
            this.updateHomeQuickActions();
        }
    }

    removeFromCheckIns(id: string) {
        const index = this.checkInsEntity.findIndex(item => item.id.toString() === id.toString());
        if (index !== -1) {
            this.checkInsEntity.splice(index, 1);
            this.updateHomeQuickActions();
        }
    }

  onProgressBarSaveClicked = (key:string ,id:string, progressValue:string, unit?:Unit) => {
    let measure = this.measureData.find(item => item.id.toString() === id.toString());
    // console.log("onProgressBarSaveClicked", key, id, progressValue, measure, this.measureData);
    let log = createMeasureLogJSON(key, progressValue, measure!, unit);
    
    this.measureLogsStateStoreService.create(log);
  }

  private fetchAndUpdateLatestStatus() {
    // 1) preserve existing action callbacks by deliverableId
    const actionMap = new Map<string, (event: any) => void>();
    this.statusEntity.forEach(item => {
      if (item.deliverableId && item.action) {
        actionMap.set(item.deliverableId, item.action);
      }
    });

    // 2) collect IDs
    const ids = this.statusEntity
      .map(item => item.deliverableId)
      .filter(id => !!id) as string[];

    // 3) fetch fresh deliverables
    this.deliverableStateStoreService.getState(
      this.bizEntityTypeStateStoreService,
      this.benBizEntityProgressStatusStoreService,
      this.kpiStateStoreService,
      this.measureStateStoreService,
      ids,
      this.benBizEntityHistoricalStatusStateStoreService,
    ).then(data => {
        // console.log("data",data);
      if (data && data.length) {
        // transform and reattach callbacks
        if(ids.length > 0){
            this.statusEntity = data.map(item => {
                const entity = transformedTovalue(item);
                const saved = actionMap.get(item.id);
                if (saved) {
                  entity.action = saved;
                }
                return entity;
              });
            }
        }
        this.updateHomeQuickActions();
      
    }).catch(err => {
      console.error('Failed to refresh deliverables', err);
    });

    this.measureStateStoreService.getState().then(data => {
      if (data && data.length) {
        // console.log("measureData", data);
        this.measureData = data;
      }
    });
  }

  private updateHomeQuickActions() {
    const updated: IHomeQuickActions = {
      ProgressStatusViewTypes: this.ProgressStatusViewTypes,
      isInCollection: this.isInCollection,
      status: this.statusEntity,
      checkIns: this.checkInsEntity,
      quickActions: this.quickActionsEntity,
    };
    // console.log("Updating home quick actions", updated);
    this.homeQuickActionsSubject.next(updated);
    this.storageService.save(LocalStorageKeys.QuickActions, JSON.stringify(updated));
  }
}

export function transformedTovalue(item: IDeliverable): IQuickActionEntity {
  let progressKPI = item.kpis.find(kpi => kpi.category === KPIAndMeasureCategories.Progress);
  let id = progressKPI?.measure?.[0]?.id;
  return {
    id: id || '',
    icon: item.icon,
    name: item.name,
    status: item.latestProgressStatus?.actual[0]?.value.actual || 0,
    checkInDueDate: item.duration.to.toDateString(),
    unit: item.unit!,
    statusIndicatorColor: StatusIndicatorColor.WARNING,
    maxValue: item.value.target,
    progressKey: item.kpis?.[0]?.statusFieldName,
    deliverableId: item.id,
    // note: .action is attached later if needed
  } as IQuickActionEntity;
}
